/**
 * @file src/_modulos/prenota/tabela/layouts/filters/TabHeader.tsx
 * @description Componente simplificado de tabs
 *
 * Princípios aplicados:
 * - KISS: Lógica simples e direta
 * - SRP: Apenas renderiza tabs
 * - YAGNI: Remove funcionalidades não utilizadas
 */

"use client";

import { useState, useRef, useLayoutEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import { useUser } from "store";
import {
  useTableStore,
  TAB_CONFIGS,
  useTabFilters,
  getTabColorByIndex,
} from "@PN/tabela";
import { TabItem } from "./TabItem";

const BOLETO_QUERY_KEY = ["prenotas", "boleto"] as const;

interface BoletoAlertaResult {
  count: number;
  docs: string[];
}

async function fetchBoletoAlerta(usuario: string): Promise<BoletoAlertaResult> {
  const res = await fetch(
    `/api/prenotas/boleto?usuario=${encodeURIComponent(usuario)}`
  );
  if (!res.ok) return { count: 0, docs: [] };
  const json = await res.json();
  if (!json?.success) return { count: 0, docs: [] };
  const docs = Array.isArray(json.docs) ? json.docs : [];
  return { count: Number(json.count) ?? 0, docs };
}

/**
 * Props do componente TabHeader
 */
interface TabHeaderProps {
  tab?: string;
  onTabChange?: (tab: string) => void;
  // Props para dados reais
  isLoading?: boolean;
  data?: any[];
  lastUpdated?: Date;
  onRefresh?: () => void;
}

/**
 * Componente TabHeader
 */
export function TabHeader({
  tab,
  onTabChange,
  isLoading = false,
  data = [],
  lastUpdated = new Date(),
  onRefresh,
}: TabHeaderProps) {
  const { activeTab, setActiveTab } = useTableStore();
  const { applyTabFilter } = useTabFilters();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoverPosition, setHoverPosition] = useState({ left: 0, width: 0 });
  const [activePosition, setActivePosition] = useState({ left: 0, width: 0 });
  const tabRefs = useRef<HTMLDivElement[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentTab = tab ?? activeTab;
  const handleTabChange = onTabChange ?? setActiveTab;
  const activeIndex = TAB_CONFIGS.findIndex((t) => t.key === currentTab);
  const { login: userLogin } = useUser();

  const { data: boletoAlerta = { count: 0, docs: [] }, isLoading: isLoadingBoleto } = useQuery({
    queryKey: [...BOLETO_QUERY_KEY, userLogin ?? ""],
    queryFn: () => fetchBoletoAlerta(userLogin ?? ""),
    enabled: !!userLogin?.trim(),
    staleTime: 2 * 60 * 1000,
  });
  const countSemBoleto = boletoAlerta.count;

  const currentLineColor = getTabColorByIndex(activeIndex).line;
  const hoveredColor =
    hoveredIndex !== null ? getTabColorByIndex(hoveredIndex) : null;

  // Função para gerar a classe de hover baseada na cor
  const getHoverBgClass = (colorConfig: typeof hoveredColor) => {
    if (!colorConfig) return "bg-accent/40";

    const colorMap = {
      indigo: "bg-indigo-500/10",
      lime: "bg-lime-500/10",
      amber: "bg-amber-500/10",
      rose: "bg-rose-500/10",
    };
    return (
      colorMap[colorConfig.primary as keyof typeof colorMap] || "bg-accent/40"
    );
  };

  const updatePositions = useCallback(() => {
    if (!containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const containerLeft = containerRect.left;

    if (activeIndex !== -1) {
      const activeEl = tabRefs.current[activeIndex];
      if (activeEl) {
        const activeRect = activeEl.getBoundingClientRect();
        setActivePosition({
          left: activeRect.left - containerLeft,
          width: activeRect.width,
        });
      }
    }

    if (hoveredIndex !== null) {
      const hoverEl = tabRefs.current[hoveredIndex];
      if (hoverEl) {
        const hoverRect = hoverEl.getBoundingClientRect();
        setHoverPosition({
          left: hoverRect.left - containerLeft,
          width: hoverRect.width,
        });
      }
    }
  }, [activeIndex, hoveredIndex]);

  useLayoutEffect(() => {
    updatePositions();
    window.addEventListener("resize", updatePositions);
    return () => window.removeEventListener("resize", updatePositions);
  }, [updatePositions]);

  const handleClick = useCallback(
    (key: string) => {
      applyTabFilter(key);
      handleTabChange(key);
    },
    [applyTabFilter, handleTabChange]
  );

  return (
    <div className="relative flex flex-row items-center bg-background border-l border-b h-15 z-20 border-border">
      <div className="relative flex items-center flex-1 min-w-0 h-full">
      {/* Indicador de hover */}
      <motion.div
        className={`absolute h-full rounded-md ${getHoverBgClass(
          hoveredColor
        )}`}
        animate={{
          left: hoverPosition.left,
          width: hoverPosition.width,
          opacity: hoveredIndex !== null ? 1 : 0,
        }}
        transition={{ type: "spring", stiffness: 400, damping: 30 }}
      />

      {/* Indicador de tab ativa */}
      <motion.div
        className={`absolute bottom-0 left-0 h-0.5 ${currentLineColor}`}
        animate={{
          left: activePosition.left,
          width: activePosition.width,
        }}
        transition={{ type: "spring", stiffness: 400, damping: 35 }}
      />

      {/* Container das tabs */}
      <div ref={containerRef} className="relative flex w-full">
        {TAB_CONFIGS.map((tabConfig, index) => (
          <div
            key={tabConfig.key}
            ref={(el) => {
              if (el) {
                tabRefs.current[index] = el;
              }
            }}
            className="relative flex-1"
          >
            <TabItem
              tabConfig={tabConfig}
              isActive={currentTab === tabConfig.key}
              onClick={() => handleClick(tabConfig.key)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              index={index}
              semBoletoCount={countSemBoleto}
              isBoletoBadgeLoading={isLoadingBoleto}
              isLoading={isLoading}
              data={data}
              lastUpdated={lastUpdated}
              onRefresh={onRefresh}
            />
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}
